import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Receipt, Users, Plus, TrendingUp, Clock, Sparkles, AlertTriangle, Activity } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { openNeedsOutcome, useNeedsOutcomeCount, NEEDS_OUTCOME_CHANGED_EVENT } from "../lib/needsOutcome";
import RevenueChart from "../components/RevenueChart";
import ClientHistoryDrawer from "../components/ClientHistoryDrawer";
import { reliabilityStyle, formatReliabilityScore } from "../lib/reliability";
import { PageBand, BandBigNumber, TileRow, StatTile, StatusChip, ListRowCard, OrangeButton } from "../components/layout/PageShell";

const fmtAud = (n) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n || 0);
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-AU", { timeZone: "Australia/Melbourne", hour: "2-digit", minute: "2-digit" });

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, readOnly } = useAuth();
  const needsOutcomeCount = useNeedsOutcomeCount();
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState({});
  const [clientList, setClientList] = useState([]);
  const [historyClient, setHistoryClient] = useState(null);

  useEffect(() => {
    const loadSummary = async () => {
      const [{ data: s }, { data: cl }] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/clients"),
      ]);
      setSummary(s);
      setClientList(cl);
      setClients(Object.fromEntries(cl.map((c) => [c.client_id, c])));
    };
    loadSummary();
    // Keep the stat cards (unpaid invoices, today's sessions) in sync after a
    // status change from the Needs-outcome pop-up, with no navigation.
    window.addEventListener(NEEDS_OUTCOME_CHANGED_EVENT, loadSummary);
    return () => window.removeEventListener(NEEDS_OUTCOME_CHANGED_EVENT, loadSummary);
  }, []);

  if (!summary) {
    return <div className="text-[#6B635C]" data-testid="dashboard-loading">Loading…</div>;
  }

  const atRisk = clientList
    .filter((c) => c.reliability?.tier === "red")
    .sort((a, b) => (a.reliability?.score ?? 0) - (b.reliability?.score ?? 0))
    .slice(0, 5);

  const today = new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" });

  const first = (user?.name || "Coach").split(" ")[0];
  const hour = new Date().getHours();
  const partOfDay = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageBand
        overlap
        flushPhone
        testid="dashboard-band"
        title={
          <div>
            <p style={{ fontSize: 12, fontWeight: 600 }} className="uppercase tracking-widest text-[#D3E8E6]">
              {today}
            </p>
            <h1 className="font-bold leading-tight mt-1" style={{ fontSize: 22, letterSpacing: "-0.02em" }}>
              Good {partOfDay}, {first}
            </h1>
          </div>
        }
      >
        <BandBigNumber
          testid="band-unpaid-total"
          value={fmtAud(summary.unpaid_total)}
          valueColor="#F4C873"
          label={
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block shrink-0" style={{ width: 8, height: 8, borderRadius: 9999, background: "#E9A94B" }} />
              {`Unpaid invoices · ${summary.unpaid_count} outstanding`}
            </span>
          }
        />
      </PageBand>

      {/* Stat tiles — every existing figure, same data source */}
      <TileRow flushPhone cols="grid-cols-2 lg:grid-cols-4">
        <StatTile
          testid="stat-today-sessions"
          label="Today's sessions"
          value={summary.today_sessions.length}
          hint={`${summary.upcoming_week_count} scheduled this week`}
        />
        <StatTile
          testid="stat-overdue"
          label="Overdue"
          value={fmtAud(summary.overdue_total || 0)}
          hint={`${summary.overdue_count || 0} past their due date`}
          tone="text-[#A8372A]"
        />
        <StatTile
          testid="stat-active-clients"
          label="Active clients"
          value={summary.active_clients}
          hint="On your roster"
        />
        <StatTile
          testid="stat-revenue"
          label="Paid (all-time)"
          value={fmtAud(summary.paid_total)}
          hint="GST inclusive"
          tone="text-[#1D6B4A]"
        />
      </TileRow>

      {/* Needs outcome — white card, terracotta left border, the screen's orange button */}
      {!readOnly && needsOutcomeCount > 0 && (
        <div
          data-testid="needs-outcome-card"
          className="bg-white border border-[#E8E1D6] flex items-center gap-3"
          style={{ borderRadius: 16, padding: 14, borderLeft: "5px solid #D9774A" }}
        >
          <Clock className="w-5 h-5 text-[#B2512A] shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-[#2A2623]" style={{ fontSize: 15 }}>
              Needs outcome ({needsOutcomeCount})
            </p>
            <p className="text-xs sm:text-sm text-[#6B635C] mt-0.5">
              Finished classes still waiting on Complete or No-show — nothing is invoiced until you mark them.
            </p>
          </div>
          <OrangeButton onClick={openNeedsOutcome} data-testid="needs-outcome-review" className="shrink-0" style={{ minHeight: 40 }}>
            Review →
          </OrangeButton>
        </div>
      )}

      {/* Quick actions */}
      <section>
        <p className="tm-label mb-3">Quick Actions</p>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => navigate("/clients?new=1")} disabled={readOnly} title={readOnly ? "Your account is read only" : undefined} data-testid="quick-add-client" className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold disabled:opacity-40">
            <Plus className="w-4 h-4" /> Add Client
          </Button>
          <Button onClick={() => navigate("/schedule?new=1")} disabled={readOnly} title={readOnly ? "Your account is read only" : undefined} data-testid="quick-add-session" variant="outline" className="border-[#E8E1D6] hover:bg-[#FFFFFF] hover:text-[#0F6B6B] hover:border-[#0F6B6B] bg-transparent disabled:opacity-40">
            <CalendarDays className="w-4 h-4" /> Schedule Session
          </Button>
          <Button onClick={() => navigate("/invoices?new=1")} disabled={readOnly} title={readOnly ? "Your account is read only" : undefined} data-testid="quick-add-invoice" variant="outline" className="border-[#E8E1D6] hover:bg-[#FFFFFF] hover:text-[#0F6B6B] hover:border-[#0F6B6B] bg-transparent disabled:opacity-40">
            <Receipt className="w-4 h-4" /> Create Invoice
          </Button>
        </div>
      </section>

      {/* Revenue chart */}
      <RevenueChart />

      {/* Today's sessions */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <div>
            <p className="tm-label">Today's Schedule</p>
            <h2 className="font-display text-2xl font-bold uppercase mt-1">Coming up</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/schedule")} data-testid="view-full-schedule" className="text-[#0F6B6B]">
            See week →
          </Button>
        </div>

        {summary.today_sessions.length === 0 ? (
          <div className="tm-card p-10 text-center" data-testid="today-sessions-list">
            <Sparkles className="w-8 h-8 mx-auto text-[#0F6B6B] mb-3" />
            <p className="text-[#2A2623] font-medium">Nothing on today.</p>
            <p className="text-[#6B635C] text-sm mt-1">Enjoy the rest. Or book a new session.</p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="today-sessions-list">
            {summary.today_sessions.map((s) => (
              <ListRowCard key={s.session_id} accent={typeColor(s.session_type)}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span style={{ fontSize: 12, fontWeight: 700 }} className="text-[#6B635C]">{fmtTime(s.starts_at)}</span>
                    <span className="text-xs text-[#6B635C] uppercase tracking-wider">{s.duration_min}min · {s.session_type}</span>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700 }} className="text-[#2A2623] mt-0.5 truncate">{clients[s.client_id]?.name || "Client"}</div>
                </div>
                <StatusChip variant={sessionChipVariant(s.status)}>{s.status}</StatusChip>
              </ListRowCard>
            ))}
          </div>
        )}
      </section>

      {/* At-Risk clients */}
      {atRisk.length > 0 && (
        <section data-testid="at-risk-section">
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="tm-label flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-[#A8372A]" /> Heads up
              </p>
              <h2 className="font-display text-2xl font-bold uppercase mt-1">At-risk clients</h2>
              <p className="text-xs text-[#6B635C] mt-1">Reliability under 50% over the last 90 days. Worth a quick check-in.</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/clients")}
              data-testid="view-all-clients"
              className="text-[#0F6B6B]"
            >
              All clients →
            </Button>
          </div>

          <div className="space-y-2.5" data-testid="at-risk-list">
            {atRisk.map((c) => {
              const style = reliabilityStyle(c.reliability?.tier);
              return (
                <ListRowCard
                  key={c.client_id}
                  accent="#A8372A"
                  as="button"
                  className="w-full text-left cursor-pointer hover:bg-[#FBFAF8] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6B6B]"
                  role="button"
                  tabIndex={0}
                  onClick={() => setHistoryClient(c)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setHistoryClient(c); } }}
                  data-testid={`at-risk-client-${c.client_id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#E8E1D6] flex items-center justify-center text-sm font-bold text-[#0F6B6B] shrink-0">
                    {c.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div style={{ fontSize: 15, fontWeight: 700 }} className="truncate">{c.name}</div>
                    <div className="text-xs text-[#6B635C] mt-0.5">
                      {c.reliability?.completed ?? 0} completed · {c.reliability?.no_shows ?? 0} no-show
                      {(c.reliability?.no_shows ?? 0) === 1 ? "" : "s"} (90d)
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-display text-xl font-black ${style.text}`} data-testid={`at-risk-score-${c.client_id}`}>
                      {formatReliabilityScore(c.reliability)}
                    </div>
                    <StatusChip variant="red">{style.label}</StatusChip>
                  </div>
                </ListRowCard>
              );
            })}
          </div>
        </section>
      )}

      <ClientHistoryDrawer
        client={historyClient}
        open={!!historyClient}
        onOpenChange={(o) => !o && setHistoryClient(null)}
      />
    </div>
  );
}

function sessionChipVariant(s) {
  switch (s) {
    case "completed": return "green";
    case "no-show": return "red";
    case "cancelled": return "grey";
    default: return "blue";
  }
}

function StatCard({ icon: Icon, label, value, hint, accent, danger, testid }) {
  let tone = "text-[#2A2623]";
  if (danger) tone = "text-[#A8372A]";
  else if (accent) tone = "text-[#0F6B6B]";
  return (
    <div className="tm-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-widest text-[#6B635C]">{label}</span>
        <Icon className={`w-4 h-4 ${danger ? "text-[#A8372A]" : accent ? "text-[#0F6B6B]" : "text-[#6B635C]"}`} />
      </div>
      <div className={`font-display text-3xl sm:text-4xl font-black ${tone}`}>{value}</div>
      <div className="text-xs text-[#6B635C] mt-2">{hint}</div>
    </div>
  );
}

export function typeColor(t) {
  if (t === "in-person") return "#0F6B6B";
  if (t === "online") return "#1F5C8F";
  return "#D9774A";
}

export function statusBadge(s) {
  switch (s) {
    case "completed": return "bg-[#E3F1E9] text-[#1D6B4A]";
    case "no-show": return "bg-[#F9E4DF] text-[#A8372A]";
    case "cancelled": return "bg-[#EEE9E0] text-[#6B635C]";
    default: return "bg-[#E1EDF7] text-[#1F5C8F]";
  }
}
